<template>
  <v-form v-model="valid" ref="form" class="dialog-content">
    <cui-subscription-editor
      v-model="localValue.item"
      class="mt-4"
      :rules="subscriptionRules"
      :baseline="baseline"
      :selection-mode="CuiBundleSelectionMode.MULTIPLE"
      :hidden-modules="hiddenModules"
      :show-required-error="valid === false"
      :labels="subscriptionEditorLabels"
      v-bind="subscriptionEditorNames"
      test-id-prefix="update-subscription-modal"
    >
      <template #addon-disabled="{ moduleLabel }">
        <span v-html="addonDisabledHint(moduleLabel)"></span>
      </template>
    </cui-subscription-editor>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, onMounted, type PropType, type Ref, ref, watch } from "vue";
import cloneDeep from "lodash/cloneDeep";
import { storeToRefs } from "pinia";
import {
  CuiBundleSelectionMode,
  CuiSubscriptionEditor,
  getHiddenSubscriptionModules,
  getModulesRemovedDuringEdit,
  setDefaultModules,
} from "@coronet/cui";
import {
  type Subscription,
  type SubscriptionRulesConfig,
  useMspStore,
} from "@/_store/msp/msp.module.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { SubscriptionModule, WorkspaceType } from "@/constants/workplaces";
import type { VuetifyFormRef } from "@/types";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useWorkspaceFormText } from "@/composables/useWorkspaceFormText";
import { isCanadaEnv } from "@/constants/general.ts";

/** What the dialog emits: the edited subscription and the modules the edit removes. */
type UpdateSubscriptionValue = Subscription & {
  modulesWhichRemovedDuringEdit?: SubscriptionModule[];
};

type UpdateSubscriptionItem = UpdateSubscriptionValue & {
  workspaceId?: string;
  /** Types of the workspaces the subscription is for: one, or every selected one in bulk mode. */
  workspaceTypes: WorkspaceType[];
};

export default defineComponent({
  components: { CuiSubscriptionEditor },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<UpdateSubscriptionItem>>,
      required: true,
    },
  },
  emits: ["update:valid", "update:localValue"],
  setup(props, { emit }) {
    const { workspace } = storeToRefs(useWorkspaceStore());
    const { subscriptionEditorLabels, subscriptionEditorNames, addonDisabledHint } =
      useWorkspaceFormText();
    const subscriptionRules: Ref<SubscriptionRulesConfig> = ref({
      allowedModules: [],
      allowedBundles: [],
    });
    const valid = ref(true);
    const form = ref<VuetifyFormRef>();
    // The subscription the dialog opened with: the editor keeps its items on offer so they can be
    // removed, and removed modules are measured against it. Copied, because the caller's bundles
    // belong to the workspaces list, which can change while the dialog is open.
    const baseline: Subscription = {
      bundles: [...(props.config.item.bundles ?? [])],
      modules: setDefaultModules(props.config.item.modules ?? {}),
    };
    const localValue = ref<DialogDataConfig<UpdateSubscriptionValue>>(
      cloneDeep({ ...props.config, item: baseline })
    );
    const hiddenModules = getHiddenSubscriptionModules(props.config.item.workspaceTypes, {
      hideSat: isCanadaEnv,
    });

    const validate = async () => {
      const validationResult = await form.value?.validate();
      return !!validationResult?.valid;
    };

    watch(
      localValue,
      (value) => {
        const newValue = cloneDeep(value);
        newValue.item.modulesWhichRemovedDuringEdit = getModulesRemovedDuringEdit(
          baseline,
          value.item
        );
        emit("update:localValue", newValue);
      },
      // Emit initial state so the dialog action receives a fully shaped payload
      // even when the user doesn't change any fields.
      { deep: true, immediate: true }
    );

    watch(
      valid,
      (newVal: boolean | null) => {
        emit("update:valid", newVal ?? true);
      },
      { immediate: false }
    );

    onMounted(async () => {
      // Use workspaceId if it's passed from the payload (when clicked on EDIT SUBSCRIPTION in table),
      // otherwise use the workspaceId from the workspace store (currently selected hierarchy) as fallback.
      const hierarchyToUse = props.config.item.workspaceId ?? workspace.value.workspaceId;
      subscriptionRules.value = await useMspStore().getWorkspaceSubscriptionRules(hierarchyToUse);
    });

    return {
      form,
      localValue,
      valid,
      validate,
      subscriptionRules,
      baseline,
      hiddenModules,
      subscriptionEditorLabels,
      subscriptionEditorNames,
      addonDisabledHint,
      CuiBundleSelectionMode,
    };
  },
});
</script>

<style scoped lang="scss">
.dialog-content {
  min-width: 650px;
  max-width: 1150px;
  width: 100%;
  margin-right: 30px;
  margin-left: 30px;
}
</style>
